"use client"

import { extractTwitterId } from "@/(admin)/modules/ui-builder/config/lib/twitter-helpers"
import { extractYouTubeId, getYouTubeThumbnail } from "@/(admin)/modules/ui-builder/config/lib/youtube-helpers"
import { useDebounce } from "@/hook/useDebounce"
import { DeleteOutlined } from "@ant-design/icons"
import { Button, Input } from "antd"
import { useEffect, useState } from "react"

type ThumbnailState = "idle" | "loading" | "success" | "error"
type Platform = "youtube" | "twitter" | "none"

type PromoVideoInputProps = {
    value?: string
    onChange?: (value: string) => void
    onRemove: () => void
    onPreview: (url: string) => void
}

export default function PromoVideoInput({ value = "", onChange, onRemove, onPreview }: PromoVideoInputProps) {
    const [thumbState, setThumbState] = useState<ThumbnailState>("idle")
    const [thumbUrl, setThumbUrl] = useState<string>("")
    const [platform, setPlatform] = useState<Platform>("none")

    const debouncedValue = useDebounce(value, 800)

    useEffect(() => {
        if (!debouncedValue) {
            setThumbState("idle")
            setThumbUrl("")
            setPlatform("none")
            return
        }


        const ytId = extractYouTubeId(debouncedValue)
        if (ytId) {
            setPlatform("youtube")
            setThumbState("loading")
            const url = getYouTubeThumbnail(ytId)
            const img = new Image()
            img.onload = () => {
                if (img.naturalWidth <= 120) {
                    setThumbState("error")
                    setThumbUrl("")
                } else {
                    setThumbState("success")
                    setThumbUrl(url)
                }
            }
            img.onerror = () => {
                setThumbState("error")
                setThumbUrl("")
            }
            img.src = url
            return
        }


        const twId = extractTwitterId(debouncedValue)
        if (twId) {
            setPlatform("twitter")
            setThumbState("success")
            setThumbUrl("")
            return
        }

        // Không khớp nền tảng nào
        setPlatform("none")
        setThumbState("error")
        setThumbUrl("")
    }, [debouncedValue])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setThumbState("idle")
        setThumbUrl("")
        setPlatform("none")
        onChange?.(newValue);
    }

    return (
        <div className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 bg-white">
            <div
                className={`relative w-[160px] h-[90px] flex-shrink-0 rounded-lg overflow-hidden border border-dashed transition-all 
                    ${thumbState === 'success' ? 'cursor-pointer hover:opacity-80 active:scale-95 border-blue-300' : 'border-gray-300 bg-gray-50'}`}
                onClick={() => thumbState === 'success' && onPreview(value)}
            >
                {thumbState === "idle" && (
                    <div className="w-full h-full flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-6 h-6 fill-gray-300">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </div>
                )}

                {thumbState === "loading" && (
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                )}

                {thumbState === "error" && (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 !border-red-200 gap-1">
                        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-red-400">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                        </svg>
                        <span className="text-[11px] text-red-500 text-center leading-tight px-1 font-medium">
                            Link YouTube / X<br />không hợp lệ
                        </span>
                    </div>
                )}


                {thumbState === "success" && platform === "youtube" && thumbUrl && (
                    <>
                        <img src={thumbUrl} alt="YouTube thumbnail" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center shadow-md">
                                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white ml-0.5">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </div>
                        </div>
                    </>
                )}


                {thumbState === "success" && platform === "twitter" && (
                    <div className="w-full h-full bg-black flex flex-col items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white mb-1">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.008 5.965H5.078z" />
                        </svg>
                        <span className="text-white/80 text-[10px] font-medium tracking-widest">POST</span>
                    </div>
                )}
                {thumbState === "success" && (
                    <div className="absolute top-1 right-1 bg-blue-600 text-white text-[9px] px-1.5 py-0.5 rounded-md shadow-lg">PREVIEW</div>
                )}
            </div>

            <div className="flex flex-1 items-center gap-2 min-w-0">
                <Input
                    value={value}
                    onChange={handleChange}
                    placeholder="https://youtube.com/... hoặc https://x.com/..."
                    status={thumbState === "error" ? "error" : ""}
                    className="flex-1"
                />
                <Button danger icon={<DeleteOutlined />} onClick={onRemove} className="flex-shrink-0" />
            </div>
        </div>
    )
}